import React from "react";
import styled, { keyframes } from "styled-components";

type Color = {
  main: string;
  right: string;
  left: string;
  shadow: string;
};

type AnimParams = {
  start: [number, number, number];
  end: [number, number, number];
};

type BoxTransforms = [number, number];

interface BoxesLoaderProps {
  className?: string;
  boxColor?: string;
  shadowColor?: string;
  duration?: number;
  size?: string;
  desktopSize?: string;
  mobileSize?: string;
}

const darken = (amount: number, color: string): string => {
  let colorString = color.replace("#", "");
  if (colorString.length === 3) {
    colorString = colorString
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const num = parseInt(colorString, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.max(0, Math.min(255, Math.floor(r * (1 - amount))));
  g = Math.max(0, Math.min(255, Math.floor(g * (1 - amount))));
  b = Math.max(0, Math.min(255, Math.floor(b * (1 - amount))));

  const newColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  return newColor;
};

const AnimBox = (animParams: AnimParams) => keyframes`
  0% {
      transform: translate(${animParams.start[0]}%, ${animParams.end[0]}%);
  }
  50% {
      transform: translate(${animParams.start[1]}%, ${animParams.end[1]}%);
  }
  100% {
      transform: translate(${animParams.start[2]}%, ${animParams.end[2]}%);
  }
`;

const Container = styled.div<{ $sizeBoxes: number; $sizeMarginBottom: number }>`
  --size: ${(props) => props.$sizeBoxes}px;
  --duration: 800ms;
  height: calc(var(--size) * 2);
  width: calc(var(--size) * 3);
  position: relative;
  transform-style: preserve-3d;
  transform-origin: 50% 50%;
  margin-bottom: ${(props) => props.$sizeMarginBottom}px;
  padding: 70px;
  transform: rotateX(60deg) rotateZ(45deg) rotateY(0deg) translateZ(0px);
`;

const StyledBox = styled.div<{
  $sizeBoxes: number;
  $animParams: AnimParams;
  $boxTransforms: BoxTransforms;
  $colors: Color;
  $duration: number;
}>`
  --size: ${(props) => props.$sizeBoxes}px;
  --duration: ${(props) => props.$duration}ms;
  width: var(--size);
  height: var(--size);
  top: 0;
  left: 0;
  position: absolute;
  transform-style: preserve-3d;
  transform: translate(${(props) => props.$boxTransforms[0]}%, ${(props) => props.$boxTransforms[1]}%);
  animation: ${(props) => AnimBox(props.$animParams)} var(--duration) linear infinite;

  & > div {
    --background: ${(props) => props.$colors.main};
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--background);
    transform: rotateY(0deg) rotateX(0deg) translateZ(calc(var(--size) / 2));
  }

  & > div:nth-child(1) {
    --background: ${(props) => props.$colors.main};
  }
  & > div:nth-child(2) {
    --background: ${(props) => props.$colors.right};
    transform: rotateY(90deg) translateZ(calc(var(--size) / 2));
  }
  & > div:nth-child(3) {
    --background: ${(props) => props.$colors.left};
    transform: rotateX(-90deg) translateZ(calc(var(--size) / 2));
  }
  & > div:nth-child(4) {
    --background: ${(props) => props.$colors.shadow};
    transform: translateZ(calc(var(--size) * 3 * -1));
  }
`;

const StyledBoxDiv = styled.div<{ $boxDivParams: any }>`
  --background: ${(props) => props.$boxDivParams.background};
  position: absolute;
  width: 100%;
  height: 100%;
  background: var(--background);
  transform: rotateY(${(props) => props.$boxDivParams.rotateY})
    rotateX(${(props) => props.$boxDivParams.rotateX})
    translateZ(${(props) => props.$boxDivParams.translateZ});
`;

const BoxesLoader: React.FC<BoxesLoaderProps> = ({
  className = `boxesloader`,
  boxColor = `#3e9ba2`,
  shadowColor = `#D8ECEB`,
  duration = 800,
  size = `64px`,
  desktopSize = ``,
  mobileSize = ``,
}) => {
  const sizeFound = desktopSize !== "" ? parseFloat(desktopSize) : parseFloat(size) * 2;
  const sizeBoxes = (sizeFound * 32) / 64;
  const sizeMarginBottom = (sizeFound * 50) / 64;

  const boxRightColor = darken(0.15, boxColor);
  const boxLeftColor = darken(0.05, boxColor);

  const colors: Color = {
    main: boxColor,
    right: boxRightColor,
    left: boxLeftColor,
    shadow: shadowColor,
  };

  const animParams: AnimParams[] = [
    { start: [100, 100, 200], end: [0, 0, 0] },
    { start: [0, 0, 100], end: [100, 0, 0] },
    { start: [100, 100, 0], end: [100, 100, 100] },
    { start: [200, 200, 100], end: [0, 100, 100] },
  ];

  const boxTransforms: BoxTransforms[] = [
    [100, 0],
    [0, 100],
    [100, 100],
    [200, 0],
  ];

  const boxDivParams = [
    {
      top: "0",
      right: "auto",
      bottom: "auto",
      left: "0",
      background: boxColor,
      rotateX: "0deg",
      rotateY: "0deg",
      translateZ: "calc(var(--size) / 2)",
    },
    {
      top: "auto",
      right: "0",
      bottom: "auto",
      left: "auto",
      background: boxRightColor,
      rotateX: "0deg",
      rotateY: "90deg",
      translateZ: "calc(var(--size) / 2)",
    },
    {
      top: "auto",
      right: "auto",
      bottom: "auto",
      left: "auto",
      background: boxLeftColor,
      rotateX: "-90deg",
      rotateY: "0deg",
      translateZ: "calc(var(--size) / 2)",
    },
    {
      top: "0",
      right: "auto",
      bottom: "auto",
      left: "0",
      background: shadowColor,
      rotateX: "0deg",
      rotateY: "0deg",
      translateZ: "calc(var(--size) * 3 * -1)",
    },
  ];

  return (
    <Container $sizeBoxes={sizeBoxes} $sizeMarginBottom={sizeMarginBottom} className={className}>
      {animParams.map((params, index) => (
        <StyledBox
          key={`${params.start.join("-")}-${params.end.join("-")}`}
          $duration={duration}
          $colors={colors}
          $sizeBoxes={sizeBoxes}
          $animParams={params}
          $boxTransforms={boxTransforms[index]}
        >
          {boxDivParams.map((divParams, divIndex) => (
            <StyledBoxDiv key={`${divParams.top}-${divParams.left}-${divIndex}`} $boxDivParams={divParams} />
          ))}
        </StyledBox>
      ))}
    </Container>
  );
};

export default BoxesLoader;